/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's copyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useEffect, useState } from 'react';
import { useAuth } from '@context/authentication';

const AUTHIEN_PICTURE_PATH = '/oauth2/oidc/picture';

/**
 * Setter used to publish the resolved picture URL (or `undefined`) to state.
 */
type UrlSetter = (url: string | undefined) => void;

/**
 * Determines the synchronous resolution for a picture URL that does not require
 * an authenticated fetch.
 *
 * @param {string | undefined | null} pictureUrl - Candidate picture URL.
 * @param {string | undefined} token - Current access token, when present.
 * @returns {{ handled: boolean; value?: string }} `handled` is true when the URL
 * can be resolved synchronously; `value` is the resolved URL (or undefined).
 */
function resolveSyncPicture(
  pictureUrl: string | undefined | null,
  token: string | undefined
): { handled: boolean; value?: string } {
  if (!pictureUrl) return { handled: true, value: undefined };
  if (!pictureUrl.includes(AUTHIEN_PICTURE_PATH)) return { handled: true, value: pictureUrl };
  if (!token) return { handled: true, value: undefined };
  return { handled: false };
}

/**
 * Reads a fetched response as a blob, throwing when the response is not ok.
 *
 * @param {Response} res - The fetch response.
 * @returns {Promise<Blob>} The response body as a blob.
 */
function responseToBlob(res: Response): Promise<Blob> {
  if (!res.ok) throw new Error(`Failed to fetch picture: ${res.status}`);
  return res.blob();
}

/**
 * Fetches an authenticated picture, publishes an object URL to state, and
 * returns a cleanup that revokes the created object URL.
 *
 * @param {string} url - The authenticated picture endpoint.
 * @param {string} token - Bearer access token.
 * @param {UrlSetter} setUrl - State setter for the resolved URL.
 * @returns {() => void} Cleanup that cancels the pending resolution.
 */
function fetchAuthenticatedPicture(url: string, token: string, setUrl: UrlSetter): () => void {
  let revoked = false;
  let objectUrl: string | undefined;
  const publish = (blob: Blob) => {
    if (revoked) return;
    objectUrl = URL.createObjectURL(blob);
    setUrl(objectUrl);
  };
  fetch(url, { headers: { Authorization: `Bearer ${token}` } })
    .then(responseToBlob)
    .then(publish)
    .catch(() => { if (!revoked) setUrl(undefined); });
  return () => {
    revoked = true;
    if (objectUrl) URL.revokeObjectURL(objectUrl);
  };
}

/**
 * Hook that resolves a profile picture URL, handling authenticated
 * picture endpoints (authien /picture) by fetching with Bearer token
 * and creating a blob URL.
 *
 * External URLs (Google/Facebook CDN) are returned as-is.
 */
export function useAuthenticatedImage(pictureUrl: string | undefined | null): string | undefined {
  const auth = useAuth();
  const [resolvedUrl, setResolvedUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    const token = auth.user?.access_token;
    const sync = resolveSyncPicture(pictureUrl, token);
    if (sync.handled) {
      setResolvedUrl(sync.value);
      return;
    }
    return fetchAuthenticatedPicture(pictureUrl as string, token as string, setResolvedUrl);
  }, [pictureUrl, auth.user?.access_token]);

  return resolvedUrl;
}
